import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  User as FirebaseUser
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  increment,
  arrayUnion,
  arrayRemove,
  DocumentReference,
  DocumentSnapshot,
  QuerySnapshot,
  CollectionReference
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject,
  uploadBytesResumable
} from 'firebase/storage';
import { 
  getMessaging, 
  getToken, 
  onMessage,
  isSupported
} from 'firebase/messaging';

// Firebase configuration - Replace with your actual config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDemoKeyForRahlaApp",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "rahla-app-demo.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "rahla-app-demo",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "rahla-app-demo.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef123456"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize messaging only in browser environment
let messaging: ReturnType<typeof getMessaging> | null = null;
if (typeof window !== 'undefined') {
  isSupported().then(supported => {
    if (supported) {
      messaging = getMessaging(app);
    }
  });
}
export { messaging };

// Collection references
export const collections = {
  users: collection(db, 'users'),
  passengers: collection(db, 'passengers'),
  drivers: collection(db, 'drivers'),
  admins: collection(db, 'admins'),
  rides: collection(db, 'rides'),
  transactions: collection(db, 'transactions'),
  coupons: collection(db, 'coupons'),
  messages: collection(db, 'messages'),
  notifications: collection(db, 'notifications'),
  supportTickets: collection(db, 'supportTickets'),
  settings: collection(db, 'settings'),
  activityLogs: collection(db, 'activityLogs'),
};

// Auth helpers
export const firebaseAuth = {
  async registerWithEmail(email: string, password: string): Promise<FirebaseUser> {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  },

  async loginWithEmail(email: string, password: string): Promise<FirebaseUser> {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  },

  async logout(): Promise<void> {
    await signOut(auth);
  },

  onAuthChange(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  },

  async updateUserProfile(user: FirebaseUser, data: { displayName?: string; photoURL?: string }) {
    await updateProfile(user, data);
  },

  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }
};

// Firestore helpers
export const firestoreHelpers = {
  // Create document with ID
  async createDocument<T>(collectionRef: CollectionReference, data: T, docId?: string): Promise<string> {
    const docData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    if (docId) {
      await setDoc(doc(collectionRef, docId), docData);
      return docId;
    } else {
      const docRef = await addDoc(collectionRef, docData);
      return docRef.id;
    }
  },

  // Get document by ID
  async getDocument<T>(collectionRef: CollectionReference, docId: string): Promise<(T & { id: string }) | null> {
    const docSnap = await getDoc(doc(collectionRef, docId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T & { id: string };
    }
    return null;
  },

  // Update document
  async updateDocument<T>(collectionRef: CollectionReference, docId: string, data: Partial<T>): Promise<void> {
    const docRef = doc(collectionRef, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete document
  async deleteDocument(collectionRef: CollectionReference, docId: string): Promise<void> {
    await deleteDoc(doc(collectionRef, docId));
  },

  // Query documents
  async queryDocuments<T>(
    collectionRef: CollectionReference,
    conditions: { field: string; operator: string; value: any }[],
    orderByField?: string,
    orderDirection?: 'asc' | 'desc',
    limitCount?: number
  ): Promise<(T & { id: string })[]> {
    let q = query(collectionRef);
    
    conditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator as any, condition.value));
    });
    
    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }
    
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T & { id: string }));
  },

  // Subscribe to document changes
  subscribeToDocument<T>(
    collectionRef: CollectionReference,
    docId: string,
    callback: (data: (T & { id: string }) | null) => void
  ) {
    return onSnapshot(doc(collectionRef, docId), (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() } as T & { id: string });
      } else {
        callback(null);
      }
    });
  },

  // Subscribe to query changes
  subscribeToQuery<T>(
    collectionRef: CollectionReference,
    conditions: { field: string; operator: string; value: any }[],
    callback: (data: (T & { id: string })[]) => void
  ) {
    let q = query(collectionRef);
    conditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator as any, condition.value));
    });
    
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T & { id: string }));
      callback(data);
    });
  },

  // Batch operations
  async batchUpdate(operations: { ref: DocumentReference; data: any }[]): Promise<void> {
    const batch = writeBatch(db);
    operations.forEach(op => {
      batch.update(op.ref, op.data);
    });
    await batch.commit();
  },

  // Increment field
  increment(value: number) {
    return increment(value);
  },

  // Server timestamp
  serverTimestamp() {
    return serverTimestamp();
  },

  // Convert Timestamp to Date
  timestampToDate(timestamp: Timestamp): Date {
    return timestamp.toDate();
  }
};

// Storage helpers
export const storageHelpers = {
  async uploadFile(file: File, path: string, onProgress?: (progress: number) => void): Promise<string> {
    const storageRef = ref(storage, path);
    
    if (onProgress) {
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          },
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });
    } else {
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    }
  },

  async deleteFile(path: string): Promise<void> {
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  },

  getFilePath(url: string): string {
    // Extract path from URL
    const match = url.match(/o\/(.+)\?/);
    return match ? decodeURIComponent(match[1]) : '';
  }
};

// Push notification helpers
export const notificationHelpers = {
  async requestPermission(): Promise<string | null> {
    if (!messaging) return null;
    
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
        });
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  },

  onMessageReceived(callback: (payload: any) => void) {
    if (!messaging) return () => {};
    return onMessage(messaging, callback);
  },

  async sendNotification(token: string, title: string, body: string, data?: any): Promise<void> {
    // This should be called from a Cloud Function
    // For client-side, we'll store the notification in Firestore
    await addDoc(collections.notifications, {
      token,
      title,
      body,
      data,
      sent: false,
      createdAt: serverTimestamp(),
    });
  }
};

// Activity logging
export const activityLogger = {
  async log(action: string, details: any, userId?: string, userType?: string): Promise<void> {
    await addDoc(collections.activityLogs, {
      action,
      details,
      userId,
      userType,
      timestamp: serverTimestamp(),
      ipAddress: null, // Would need server-side implementation
    });
  }
};

export default app;
